import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, ArrowRight, CheckCircle2, Clock, Sparkles, Home, Building2, Scissors, Truck, Timer, Shield, Award, Users, Navigation, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const HERO_IMG = "https://images.unsplash.com/photo-1762811054950-b74e0a055c80?w=1400&q=80";
const LOGO_MAIN = "https://customer-assets.emergentagent.com/job_09ef4963-c96f-46c6-a08d-052afb978ee9/artifacts/j66rn420_Gleam%20Guru%20Cleaning%20%5BLogo%20Design%5D%20No%20Gleam.png";

const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

const CITIES = [
  { name: 'Portland', sub: 'Full residential & commercial coverage', primary: true },
  { name: 'Vancouver, WA', sub: 'Residential and commercial cleaning available' },
  { name: 'Beaverton', sub: 'Residential and commercial cleaning available' },
  { name: 'Hillsboro', sub: 'Residential and commercial cleaning available' },
  { name: 'Tigard', sub: 'Residential and commercial cleaning available' },
  { name: 'Lake Oswego', sub: 'Residential and commercial cleaning available' },
  { name: 'Gresham', sub: 'Residential and commercial cleaning available' },
  { name: 'Milwaukie', sub: 'Residential and commercial cleaning available' },
];

const SERVICES = [
  { name: 'Residential Cleaning', icon: Home, slug: 'maintenance-clean-portland', desc: 'Weekly, biweekly, or monthly home cleaning' },
  { name: 'Deep Reset Cleaning', icon: Sparkles, slug: 'deep-reset-portland', desc: 'Top-to-bottom deep clean transformation' },
  { name: 'Move-Out Cleaning', icon: Truck, slug: 'move-out-move-in-clean-portland', desc: 'Every surface spotless for move day' },
  { name: 'Carpet Cleaning', icon: Scissors, slug: 'carpet-cleaning-portland', desc: 'Professional deep carpet treatment' },
  { name: 'Airbnb Cleaning', icon: Clock, slug: 'airbnb-turnover-cleaning-portland', desc: 'Guest-ready turnover cleaning' },
  { name: 'Office Cleaning', icon: Building2, slug: 'office-cleaning-portland', desc: 'Professional workspace maintenance' },
  { name: 'Trash-Out Services', icon: Truck, slug: 'trash-out-property-cleanout-portland', desc: 'Property cleanout and restoration' },
];

const MAP_CITIES = [
  { name: 'Vancouver, WA', top: '12%', left: '52%' },
  { name: 'Portland', top: '35%', left: '55%', primary: true },
  { name: 'Gresham', top: '38%', left: '78%' },
  { name: 'Beaverton', top: '42%', left: '30%' },
  { name: 'Hillsboro', top: '45%', left: '10%' },
  { name: 'Milwaukie', top: '55%', left: '60%' },
  { name: 'Lake Oswego', top: '62%', left: '45%' },
  { name: 'Tigard', top: '65%', left: '30%' },
];

const ServiceAreaPage = () => {
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistZip, setWaitlistZip] = useState('');
  const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);
  const [waitlistLoading, setWaitlistLoading] = useState(false);

  const handleWaitlist = async (e) => {
    e.preventDefault();
    if (!waitlistEmail || !waitlistZip) return;
    setWaitlistLoading(true);
    try {
      await axios.post(`${API_URL}/api/service-areas/waitlist`, { email: waitlistEmail, zip_code: waitlistZip });
      setWaitlistSubmitted(true);
    } catch {
      setWaitlistSubmitted(true);
    } finally {
      setWaitlistLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 bg-white" data-testid="service-area-page">

      {/* ═══ SECTION 1: HERO ═══ */}
      <section className="relative py-24 md:py-32 overflow-hidden bg-zinc-950" data-testid="sa-hero">
        <div className="absolute inset-0 opacity-30">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/90 to-zinc-950/70" />

        <div className="relative max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}>
            <Badge className="bg-[#F56F05]/15 text-[#F56F05] border-[#F56F05]/30 text-xs font-semibold mb-6 px-3 py-1">
              <Navigation className="h-3.5 w-3.5 mr-1.5" /> Portland Metro Area
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white font-heading mb-5 tracking-tight" data-testid="sa-hero-heading">
              Proudly Serving Portland<br />
              <span className="text-[#F56F05]">& Surrounding Communities</span>
            </h1>
            <p className="text-base lg:text-lg text-zinc-400 max-w-2xl mx-auto leading-relaxed">
              Gleam Guru provides premium residential and commercial cleaning services throughout the Portland metro area.
            </p>
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" className="w-full"><path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H0Z" fill="white"/></svg>
        </div>
      </section>

      {/* ═══ SECTION 2: CORE SERVICE AREA ═══ */}
      <section className="py-16 md:py-20 bg-white" data-testid="sa-core-region">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div {...fadeUp}>
            <p className="text-sm font-semibold text-[#F56F05] uppercase tracking-widest mb-3">Coverage</p>
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 font-heading mb-5">Our Core Service Region</h2>
            <p className="text-zinc-600 leading-relaxed max-w-2xl mx-auto">
              Gleam Guru proudly serves the Portland metropolitan area, including Portland proper, Vancouver WA, and surrounding suburbs. We maintain a structured service territory to ensure reliable arrival windows, consistent cleaning quality, and the premium experience our clients expect.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═══ SECTION 3: CITIES WE SERVE ═══ */}
      <section className="py-16 md:py-20 bg-zinc-50" data-testid="sa-cities">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp} className="text-center mb-10">
            <p className="text-sm font-semibold text-[#F56F05] uppercase tracking-widest mb-3">Locations</p>
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 font-heading">Cities We Serve</h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {CITIES.map((city, i) => (
              <motion.div key={city.name} {...fadeUp} transition={{ delay: i * 0.05 }}>
                <div className={`group relative p-5 rounded-2xl border-2 transition-all duration-300 h-full ${
                  city.primary
                    ? 'bg-[#FFF7ED] border-[#F56F05]/30 shadow-md shadow-orange-100'
                    : 'bg-white border-zinc-100 hover:border-[#F56F05]/40 hover:shadow-md hover:shadow-orange-50'
                }`} data-testid={`city-card-${city.name.toLowerCase().replace(/[\s,]+/g, '-')}`}>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 transition-colors ${
                    city.primary ? 'bg-[#F56F05] text-white' : 'bg-zinc-100 text-zinc-400 group-hover:bg-[#F56F05] group-hover:text-white'
                  }`}>
                    <MapPin className="h-5 w-5" />
                  </div>
                  <h3 className="font-bold text-zinc-900 text-lg mb-1">{city.name}</h3>
                  <p className="text-sm text-zinc-500 leading-relaxed">{city.sub}</p>
                  {city.primary && <Badge className="mt-3 bg-[#F56F05] text-white text-[10px]">Headquarters</Badge>}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SECTION 4: SERVICE AREA MAP ═══ */}
      <section className="py-16 md:py-20 bg-white" data-testid="sa-map">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp} className="text-center mb-10">
            <p className="text-sm font-semibold text-[#F56F05] uppercase tracking-widest mb-3">Coverage Map</p>
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 font-heading">Our Service Coverage</h2>
          </motion.div>

          <motion.div {...fadeUp} transition={{ delay: 0.1 }}>
            {/* Desktop map */}
            <div className="relative bg-zinc-950 rounded-3xl overflow-hidden shadow-2xl border border-zinc-800 hidden md:block" style={{ aspectRatio: '16/9' }}>
              {/* Map background pattern */}
              <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle, #F56F05 1px, transparent 1px)', backgroundSize: '32px 32px' }} />

              {/* Coverage area blob */}
              <div className="absolute inset-[15%] rounded-[40%] bg-[#F56F05]/10 border-2 border-[#F56F05]/20 border-dashed" />

              {/* City markers */}
              {MAP_CITIES.map((city) => (
                <div key={city.name} className="absolute flex flex-col items-center" style={{ top: city.top, left: city.left, transform: 'translate(-50%, -50%)' }}>
                  <div className={`w-4 h-4 rounded-full shadow-lg ${city.primary ? 'bg-[#F56F05] ring-4 ring-[#F56F05]/30 w-5 h-5' : 'bg-white ring-2 ring-[#F56F05]/50'}`} />
                  <span className={`mt-1.5 text-[11px] font-semibold whitespace-nowrap ${city.primary ? 'text-[#F56F05] text-xs' : 'text-zinc-400'}`}>
                    {city.name}
                  </span>
                </div>
              ))}

              {/* Legend */}
              <div className="absolute bottom-4 left-4 flex items-center gap-4 bg-zinc-900/80 backdrop-blur-sm rounded-xl px-4 py-2.5 border border-zinc-700">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#F56F05]" />
                  <span className="text-xs text-zinc-300 font-medium">Headquarters</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-white ring-1 ring-[#F56F05]/50" />
                  <span className="text-xs text-zinc-300 font-medium">Service Area</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-3 rounded border border-dashed border-[#F56F05]/40" />
                  <span className="text-xs text-zinc-300 font-medium">Coverage Zone</span>
                </div>
              </div>

              {/* Gleam Guru branding */}
              <div className="absolute top-4 right-4">
                <img src={LOGO_MAIN} alt="Gleam Guru" className="h-8 w-auto opacity-60" />
              </div>
            </div>

            {/* Mobile coverage summary */}
            <div className="md:hidden bg-zinc-950 rounded-2xl p-6 border border-zinc-800 text-center">
              <MapPin className="h-8 w-8 text-[#F56F05] mx-auto mb-3" />
              <h3 className="text-white font-bold text-lg mb-2">Portland Metro Coverage</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">
                We serve Portland, Vancouver WA, Beaverton, Hillsboro, Tigard, Lake Oswego, Gresham, and Milwaukie with reliable arrival windows across the entire metro area.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══ SECTION 5: SERVICES AVAILABLE ═══ */}
      <section className="py-16 md:py-20 bg-zinc-50" data-testid="sa-services">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp} className="text-center mb-10">
            <p className="text-sm font-semibold text-[#F56F05] uppercase tracking-widest mb-3">What We Offer</p>
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 font-heading">Services Available In Your Area</h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {SERVICES.map((svc, i) => (
              <motion.div key={svc.name} {...fadeUp} transition={{ delay: i * 0.04 }}>
                <Link to={`/services/${svc.slug}`}
                  className="group block p-5 rounded-2xl bg-white border-2 border-zinc-100 hover:border-[#F56F05]/40 hover:shadow-lg hover:shadow-orange-50 transition-all duration-300 h-full"
                  data-testid={`sa-svc-${svc.slug}`}>
                  <div className="w-10 h-10 rounded-xl bg-zinc-100 text-zinc-400 group-hover:bg-[#F56F05] group-hover:text-white flex items-center justify-center mb-3 transition-colors">
                    <svc.icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-bold text-zinc-900 mb-1">{svc.name}</h3>
                  <p className="text-sm text-zinc-500 leading-relaxed mb-3">{svc.desc}</p>
                  <span className="inline-flex items-center text-[#F56F05] text-xs font-semibold group-hover:gap-1.5 transition-all">
                    Learn more <ArrowRight className="h-3 w-3 ml-0.5" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SECTION 6: EXPANSION WAITLIST ═══ */}
      <section className="py-16 md:py-20 bg-zinc-950" data-testid="sa-waitlist">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <motion.div {...fadeUp}>
            <Mail className="h-10 w-10 text-[#F56F05] mx-auto mb-4" />
            <h2 className="text-3xl md:text-4xl font-bold text-white font-heading mb-3">Not In Our Service Area Yet?</h2>
            <p className="text-zinc-400 leading-relaxed mb-8 max-w-xl mx-auto">
              Gleam Guru is expanding across the Pacific Northwest. Join our waitlist and be the first to know when we arrive in your neighborhood.
            </p>

            {waitlistSubmitted ? (
              <div className="bg-[#F56F05]/10 border border-[#F56F05]/30 rounded-2xl p-6" data-testid="waitlist-success">
                <CheckCircle2 className="h-8 w-8 text-[#F56F05] mx-auto mb-3" />
                <p className="text-white font-semibold">You're on the list!</p>
                <p className="text-sm text-zinc-400 mt-1">We'll notify you as soon as we expand to your area.</p>
              </div>
            ) : (
              <form onSubmit={handleWaitlist} className="flex flex-col sm:flex-row items-center gap-3 max-w-lg mx-auto" data-testid="waitlist-form">
                <Input value={waitlistEmail} onChange={e => setWaitlistEmail(e.target.value)} type="email"
                  placeholder="Your email" className="h-12 bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-500 rounded-xl" data-testid="waitlist-email" />
                <Input value={waitlistZip} onChange={e => setWaitlistZip(e.target.value)} maxLength={5}
                  placeholder="ZIP code" className="h-12 bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-500 rounded-xl sm:w-32" data-testid="waitlist-zip" />
                <Button type="submit" disabled={waitlistLoading}
                  className="bg-[#F56F05] hover:bg-[#C2410C] text-white font-bold px-6 h-12 rounded-xl whitespace-nowrap" data-testid="waitlist-submit">
                  {waitlistLoading ? 'Joining...' : 'Join Waitlist'}
                </Button>
              </form>
            )}
          </motion.div>
        </div>
      </section>

      {/* ═══ SECTION 7: FINAL CTA ═══ */}
      <section className="relative py-20 md:py-28 bg-zinc-950 border-t border-white/5 overflow-hidden" data-testid="sa-final-cta">
        <div
          className="absolute inset-0 opacity-[0.07] pointer-events-none"
          style={{ backgroundImage: 'radial-gradient(circle, #F56F05 1px, transparent 1px)', backgroundSize: '28px 28px' }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-[#F56F05] opacity-[0.1] blur-[140px] pointer-events-none"
          animate={{ scale: [1, 1.15, 1] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        />
        <div className="relative max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <motion.div {...fadeUp}>
            <img src={LOGO_MAIN} alt="Gleam Guru" className="h-14 w-auto mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-extrabold text-white font-heading tracking-tight mb-4">
              Gleam With The <span className="bg-gradient-to-br from-[#FFB85C] via-[#F56F05] to-[#C2410C] bg-clip-text text-transparent">Masters Of Clean</span>
            </h2>
            <p className="text-zinc-400 text-sm mb-8 max-w-lg mx-auto">
              Get in touch today and experience what premium cleaning feels like. No commitments, no pressure — just a conversation.
            </p>
            <div className="flex justify-center">
              <motion.div whileHover={{ scale: 1.03, y: -2 }} whileTap={{ scale: 0.98 }}>
                <Button
                  asChild
                  className="relative overflow-hidden bg-[#F56F05] hover:bg-[#C2410C] text-white px-10 py-7 text-base rounded-full font-bold shadow-[0_20px_50px_-12px_rgba(245,111,5,0.55)] hover:shadow-[0_25px_60px_-12px_rgba(245,111,5,0.7)] transition-all group/cta"
                  data-testid="sa-cta-quote"
                >
                  <Link to="/?service=#quote-form">
                    <span className="relative z-10 flex items-center">
                      Let&apos;s Get Your Space Gleaming <ArrowRight className="ml-2 w-4 h-4 group-hover/cta:translate-x-1 transition-transform" />
                    </span>
                    <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent -translate-x-full group-hover/cta:translate-x-full transition-transform duration-700 pointer-events-none" />
                  </Link>
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServiceAreaPage;
